<template>
  <div class="modal d-block" tabindex="-1" @click.self="close">
    <div class="modal-dialog modal-xl">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <template v-if="isCreateMode">
              {{ t("ecolabels.labels.workbench.create_title") }}
            </template>
            <template v-else>
              {{ t("ecolabels.labels.workbench.title") }}
              {{ currentLabel?.number }}
              <span
                v-if="currentLabel"
                class="badge ms-2"
                :class="labelStatusPillClass(currentLabel.status)"
              >
                {{
                  t(
                    `ecolabels.labels.statuses.${currentLabel.status.toLowerCase()}`
                  )
                }}
              </span>
            </template>
          </h5>
          <button
            type="button"
            class="btn-close"
            :aria-label="t('common.close')"
            @click="close"
          />
        </div>

        <div class="modal-body">
          <template v-if="isCreateMode">
            <div v-if="showSellerSelector" class="mb-3">
              <label :for="sellerId" class="form-label">
                {{ t("ecolabels.labels.workbench.seller_company") }}
              </label>
              <SearchableSelectComponent
                :inputId="sellerId"
                :modelValue="sellerCompanyId"
                :options="sellerOptions"
                :placeholder="t('common.search')"
                :isLoading="loadingSellers"
                :disabled="busy || loadingSellers || sellers.length === 0"
                autoOpen
                clearOnFocus
                @update:modelValue="sellerCompanyId = $event ?? null"
              />
              <small
                v-if="!loadingSellers && sellers.length === 0"
                class="form-text text-muted"
              >
                {{
                  t(
                    isAdmin
                      ? "ecolabels.labels.workbench.no_sellers"
                      : "ecolabels.labels.workbench.no_own_sellers"
                  )
                }}
              </small>
            </div>
          </template>

          <LoadingIndicatorComponent
            v-if="!isCreateMode && currentLabel === null"
          />

          <div v-else class="row g-4">
            <div class="col-lg-7">
              <div v-if="chatCompanyId !== null" class="label-chat mb-3">
                <AiChatCard
                  :companyId="chatCompanyId"
                  :endpoint="chatEndpoint"
                  :greeting="chatGreeting"
                  :answerLocally="isCreateMode ? requireDocuments : undefined"
                  @analysis-complete="handleChatResult"
                />
              </div>
              <h6 class="mb-2">
                {{ t("ecolabels.labels.workbench.sources") }}
              </h6>
              <p
                v-if="
                  currentLabel === null || currentLabel.sources.length === 0
                "
                class="text-body-secondary small"
              >
                {{ t("ecolabels.labels.workbench.no_sources") }}
              </p>
              <template v-if="currentLabel !== null">
                <LabelSourceCard
                  v-for="source in currentLabel.sources"
                  :key="source.id"
                  class="mb-3"
                  :labelId="currentLabel.id"
                  :source
                  :editable="isDraft"
                  :defaultExpanded="
                    source.problems.length > 0 || addedSourceIds.has(source.id)
                  "
                  :catalog
                  :catalogLoading
                  @changed="handleChanged"
                  @update:pending="setSourcePending(source.id, $event)"
                  @remove="removeSource(source)"
                />
              </template>
            </div>
            <div class="col-lg-5">
              <LabelPanel
                v-if="currentLabel !== null"
                :label="currentLabel"
                :isAdmin
                :sourcesPending
                @changed="handleChanged"
                @deleted="handleDeleted"
                @reopened="handleReopened"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="modal-backdrop fade show" />
</template>

<script setup lang="ts">
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import SearchableSelectComponent from "@/components/common/SearchableSelectComponent.vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import AiChatCard from "@/dashboard/inventory_management/common/AiChatCard.vue";
import { onKeyStroke } from "@vueuse/core";
import { computed, onMounted, ref, useId } from "vue";
import { useI18n } from "vue-i18n";
import { ecolabelsApi } from "../api/ecolabelsApi";
import type {
  AuthorizedSellerCompany,
  EcolabelDevice,
} from "../api/ecolabelsTypes";
import LabelPanel from "./LabelPanel.vue";
import LabelSourceCard from "./LabelSourceCard.vue";
import { labelStatusPillClass } from "./labelDisplay";
import {
  labelChatEndpoint,
  labelCreateChatEndpoint,
  labelsApi,
} from "./labelsApi";
import type { Label, LabelSource } from "./labelsTypes";
import { useBusyAction } from "./useBusyAction";

const props = defineProps<{
  label: Label | null;
  isAdmin: boolean;
  catalog: EcolabelDevice[];
  catalogLoading: boolean;
}>();

const emit = defineEmits<{
  close: [];
  changed: [];
}>();

const { t } = useI18n();
const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();
const sellerId = useId();

const currentLabelId = ref(props.label?.id ?? null);
const currentLabel = ref<Label | null>(props.label);
const { busy, run } = useBusyAction();
const sellers = ref<AuthorizedSellerCompany[]>([]);
const loadingSellers = ref(false);
const sellerCompanyId = ref<number | null>(null);
const addedSourceIds = ref<ReadonlySet<number>>(new Set());
const pendingSourceIds = ref<ReadonlySet<number>>(new Set());
let refreshGeneration = 0;

const sourcesPending = computed(() =>
  (currentLabel.value?.sources ?? []).some((source) =>
    pendingSourceIds.value.has(source.id)
  )
);
const isCreateMode = computed(() => currentLabelId.value === null);
const isDraft = computed(() => currentLabel.value?.status === "DRAFT");
const showSellerSelector = computed(
  () => props.isAdmin || sellers.value.length !== 1
);
const sellerOptions = computed(() =>
  sellers.value.map((company) => ({ label: company.name, value: company.id }))
);
const chatCompanyId = computed(() => {
  if (isCreateMode.value) return sellerCompanyId.value;
  return isDraft.value ? (currentLabel.value?.company_id ?? null) : null;
});
const chatEndpoint = computed(() =>
  currentLabelId.value === null
    ? labelCreateChatEndpoint()
    : labelChatEndpoint(currentLabelId.value)
);
const chatGreeting = computed(() =>
  t(
    isCreateMode.value
      ? "ai_chat.greetings.ecolabel_label_new"
      : "ai_chat.greetings.ecolabel_label"
  )
);

function requireDocuments({ files }: { files: File[] }): string | null {
  return files.length === 0 ? t("ai_chat.ecolabel_label_needs_document") : null;
}

onMounted(() => (isCreateMode.value ? loadSellers() : refreshLabel()));

onKeyStroke("Escape", close);

function close() {
  emit("close");
}

async function refreshLabel() {
  if (currentLabelId.value === null) return;
  const generation = ++refreshGeneration;
  try {
    const { data } = await labelsApi.getLabel(currentLabelId.value);
    if (generation === refreshGeneration) currentLabel.value = data;
  } catch (error) {
    globalErrorHandler(error);
  }
}

async function loadSellers() {
  loadingSellers.value = true;
  try {
    const { data } = await ecolabelsApi.listAuthorizedSellerCompanies();
    sellers.value = data.companies;
    sellerCompanyId.value = data.companies[0]?.id ?? null;
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    loadingSellers.value = false;
  }
}

function handleChatResult(result: unknown) {
  if (result === null) return;
  if (isCreateMode.value) {
    void showCreatedLabel(result as Label);
    return;
  }
  const updated = result as Label;
  rememberAddedSources(updated);
  currentLabel.value = updated;
  emit("changed");
}

async function showCreatedLabel(created: Label) {
  currentLabelId.value = created.id;
  currentLabel.value = created;
  globalSuccessHandler(t("ecolabels.labels.created"), "");
  emit("changed");
  await refreshLabel();
}

function rememberAddedSources(updated: Label) {
  const known = new Set(currentLabel.value?.sources.map((source) => source.id));
  const added = updated.sources
    .map((source) => source.id)
    .filter((id) => !known.has(id));
  addedSourceIds.value = new Set([...addedSourceIds.value, ...added]);
}

async function removeSource(source: LabelSource) {
  const labelId = currentLabelId.value;
  if (labelId === null || currentLabel.value === null) return;
  const isLastSource = currentLabel.value.sources.length === 1;
  const confirmKey = isLastSource
    ? "ecolabels.labels.source.confirm_remove_last"
    : "ecolabels.labels.source.confirm_remove";
  if (!window.confirm(t(confirmKey, { file: source.file_name }))) return;

  await run(async () => {
    const response = await labelsApi.removeSource(labelId, source.id);
    if (response.status === 204) {
      globalSuccessHandler(t("ecolabels.labels.deleted"), "");
      handleDeleted();
      return;
    }
    globalSuccessHandler(t("ecolabels.labels.source.removed"), "");
    handleChanged();
  });
}

function setSourcePending(sourceId: number, pending: boolean) {
  const ids = new Set(pendingSourceIds.value);
  if (pending) ids.add(sourceId);
  else ids.delete(sourceId);
  pendingSourceIds.value = ids;
}

function handleChanged() {
  emit("changed");
  void refreshLabel();
}

function handleDeleted() {
  emit("changed");
  emit("close");
}

function handleReopened(draft: Label) {
  currentLabelId.value = draft.id;
  currentLabel.value = draft;
  addedSourceIds.value = new Set();
  emit("changed");
}
</script>

<style scoped lang="scss">
.label-chat :deep(.chat-thread) {
  max-height: 16rem;
}
</style>
